import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { AbsenceProposal } from '../../../models/AbsenceProposal';
import { Modal } from '@prio365/prio365-react-library';
import { fullDateFormatFormatString } from '../../../util';
import {
  deleteAbsenceProposalMe,
  fetchAbsencesMe,
  revokeAbsenceProposal,
} from '../actions';
import { useDispatch } from 'react-redux';

export type AbsenceProposalFormModalType = 'delete' | 'revoke' | undefined;

interface AbsenceProposalFormModalProps {
  className?: string;
  absenceProposal: AbsenceProposal;
  setDrawerOpen?: (open: boolean) => void;
  visible: boolean;
  setModalType: (visible: AbsenceProposalFormModalType) => void;
  type: AbsenceProposalFormModalType;
}

export const AbsenceProposalFormModal: React.FC<
  AbsenceProposalFormModalProps
> = (props) => {
  //#region ------------------------------ Defaults
  const {
    className,
    absenceProposal,
    setDrawerOpen,
    visible,
    setModalType,
    type,
  } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const translationName = type === 'delete' ? 'deleteAbsence' : 'revokeAbsence';
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const onDelete = useCallback(() => {
    dispatch(
      deleteAbsenceProposalMe(
        absenceProposal.absenceProposalId,
        absenceProposal
      )
    );
    dispatch(fetchAbsencesMe());
    if (setDrawerOpen) {
      setDrawerOpen(false);
    }
  }, [dispatch, absenceProposal, setDrawerOpen]);

  const onRevoke = useCallback(() => {
    dispatch(
      revokeAbsenceProposal(absenceProposal.absenceProposalId, absenceProposal)
    );
    dispatch(fetchAbsencesMe());
    if (setDrawerOpen) {
      setDrawerOpen(false);
    }
  }, [dispatch, absenceProposal, setDrawerOpen]);

  const handleModalOk = useCallback(() => {
    if (type === 'delete') {
      onDelete();
      setModalType(undefined);
    }
    if (type === 'revoke') {
      onRevoke();
      setModalType(undefined);
    }
  }, [type, onDelete, onRevoke, setModalType]);
  //#endregion

  return (
    <Modal
      className={classNames(className)}
      title={t(`absences:${translationName}.confirmationDialog.title`)}
      visible={visible}
      okText={t(`absences:${translationName}.confirmationDialog.okText`)}
      cancelText={t(
        `absences:${translationName}.confirmationDialog.cancelText`
      )}
      onOk={handleModalOk}
      onClose={() => setModalType(undefined)}
    >
      {absenceProposal &&
        absenceProposal.from &&
        absenceProposal.to &&
        t(`absences:${translationName}.confirmationDialog.content`, {
          period:
            absenceProposal.from === absenceProposal.to
              ? fullDateFormatFormatString(absenceProposal.from)
              : `${fullDateFormatFormatString(absenceProposal.from)} ${t(
                  'common:to'
                )} ${fullDateFormatFormatString(absenceProposal.to)}`,
          absenceType: t(`absences:types.${absenceProposal.absenceType}`),
        })}
    </Modal>
  );
};

export default AbsenceProposalFormModal;
